import WildStickyRecorder from 'integration/wild-sticky-record';
import { GameType, GFCustomLayers } from 'names/do-names';
import { Container, Matrix } from 'pixi.js';
import Components from 'slotworks/components/components';
import AnimateSymbolBehaviour from 'slotworks/components/matrix/symbol/behaviours/animate-symbol-behaviour';
import DimSymbolBehaviour from 'slotworks/components/matrix/symbol/behaviours/dim-symbol-behaviour';
import LandAnimateSymbolBehaviour from 'slotworks/components/matrix/symbol/behaviours/land-animate-symbol-behaviour';
import SymbolComponent from 'slotworks/components/matrix/symbol/symbol-component';
import { Zoom } from 'slotworks/graphics/animation/base-animations';
import canvasManager, { Orientation } from 'slotworks/graphics/canvas/canvas-manager';
import graphicsManager from 'slotworks/graphics/graphics-manager';
import Layers from 'slotworks/graphics/layers/layers';
import betModel from 'slotworks/model/bets/bet-model';
import gameState from 'slotworks/model/game-state';
import soundManager from 'slotworks/sound/sound-manager';
import state from 'slotworks/state-machine/states/state';
import Contract from 'slotworks/utils/contract';
import Point from 'slotworks/utils/geom/point';
import Timer from 'slotworks/utils/timer';
import * as TweenJS from 'slotworks/utils/tween';
import GameInit from './game-init';

class WildStickySymbolData {
    constructor(container: Container, bulletContainer: Container, position: Point, stickyWild: SymbolComponent) {
        this.container = container;
        this.position = position;
        this.stickyWild = stickyWild;
        this.bulletContainer = bulletContainer;
    }

    public container: Container;
    public position: Point;
    public bulletContainer: Container;
    public stickyWild: SymbolComponent;
}

export default class WildSticky extends state {
    public static positions: { [key: number]: Point } = {
        1: new Point(1, 1),
        2: new Point(1, 2),
        3: new Point(1, 3),
        4: new Point(2, 1),
        5: new Point(2, 2),
        6: new Point(2, 3),
        7: new Point(3, 1),
        8: new Point(3, 2),
        9: new Point(3, 3),
        10: new Point(4, 1),
        11: new Point(4, 2),
        12: new Point(4, 3),
        13: new Point(5, 1),
        14: new Point(5, 2),
        15: new Point(5, 3)
    };

    public static currentBaseWildStickyDatas: Map<string, WildStickySymbolData> = new Map<string, WildStickySymbolData>();
    public static currentFreeSpinWildStickyDatas: Map<string, WildStickySymbolData> = new Map<string, WildStickySymbolData>();
    public static gunLoadTime = 0;
    public static remaingTextChangeTime = 1000;
    public static gunFireTime = 1250;
    public static beltContainerZoomOutTime = 1600;
    public static bulletFlyTime = 1000;
    public static bulletZoomInTime = 800;
    public static landedWildCount: number = 1;

    public static tweens: TweenJS.Tween[] = [];
    public static wildStickyRecorder: WildStickyRecorder;

    public onEnter() {
        this.onSpinResponse();
    }

    public static killAllTweens() {
        for (let i = 0; i < this.tweens.length; i++) {
            TweenJS.remove(this.tweens[i]);
        }
    }

    public static onSpin() {
        this.landedWildCount = 0;
        this.currentBaseWildStickyDatas.forEach(data => {
            const key = `symbol_${data.position.x}_${data.position.y}`;
            const text = this.currentBaseWildStickyDatas.get(key).bulletContainer.getChildByName('remain') as PIXI.extras.BitmapText;
            const count = parseInt(text?.text);
            if (count - 1 === -1) {
                this.destroyContainerById(key, false);
            }
        });
    }

    public static onOrientationChange() {
        this.killAllTweens();
        for (const symbol of Components.matrix.getAllSymbols()) {
            symbol.setVisible(true);
        }
        this.wildStickyRecorder = GameInit.wildStickyRecorder;

        const isFreepinSticky = GameInit.gameType === GameType.freeSpin;
        const currentWildDatas: Map<string, WildStickySymbolData> = isFreepinSticky
            ? this.currentFreeSpinWildStickyDatas
            : this.currentBaseWildStickyDatas;

        currentWildDatas.forEach(data => {
            const id = `symbol_${data.position.x}_${data.position.y}`;
            this.clearStuckSymbolsById(data.position.x, data.position.y);
            const container = Layers.Custom.get(GFCustomLayers.WILD_STICKY).container as Container;
            const currentContainer = container.getChildByName(id) as Container;

            if (currentContainer) {
                currentContainer.destroy({ children: true });
            }
        });

        if (this.wildStickyRecorder?.persistedProgress?.WILD_SPIN_DATA) {
            const data = JSON.parse(this.wildStickyRecorder.persistedProgress.WILD_SPIN_DATA);
            if (isFreepinSticky) {
                this.currentFreeSpinWildStickyDatas = new Map<string, WildStickySymbolData>();
                this.onRecoveryFreespinStickyWild(data);
            } else {
                this.currentBaseWildStickyDatas = new Map<string, WildStickySymbolData>();
                this.onRecoveryBaseStickyWild(data);
            }
        }
    }

    public onSpinResponse() {
        if (GameInit.gameType === GameType.freeSpin) {
            Timer.setTimeout(() => {
                this.complete();
            }, 250);
        } else {
            this.onBaseSpinResponse();
        }
    }

    public onBaseSpinResponse() {
        WildSticky.currentBaseWildStickyDatas.forEach(data => {
            const key = `symbol_${data.position.x}_${data.position.y}`;
            const text = WildSticky.currentBaseWildStickyDatas.get(key).bulletContainer.getChildByName('remain') as PIXI.extras.BitmapText;
            const count = parseInt(text?.text);
            if (count - 1 !== -1) {
                WildSticky.playBaseWildStickyAnimation(key);
            }
        });
        if (WildSticky.currentBaseWildStickyDatas.size <= 0) {
            Timer.setTimeout(() => {
                this.complete();
            }, 250);
        } else {
            Timer.setTimeout(() => {
                this.complete();
            }, 2000);
        }
    }

    public static destroyContainerById(id: string, freespin: boolean) {
        const container = Layers.Custom.get(GFCustomLayers.WILD_STICKY).container as Container;
        const currentContainer = container.getChildByName(id) as Container;
        if (currentContainer) {
            currentContainer.destroy({ children: true });
        }
        let pos;
        if (freespin) {
            this.currentFreeSpinWildStickyDatas.get(id)?.stickyWild?.destroy();
            pos = this.currentFreeSpinWildStickyDatas.get(id)?.position;
            this.clearStuckSymbolsById(pos?.x, pos?.y);
            this.currentFreeSpinWildStickyDatas.delete(id);
        } else {
            pos = this.currentBaseWildStickyDatas.get(id)?.position;
            this.clearStuckSymbolsById(pos?.x, pos?.y);
            this.currentBaseWildStickyDatas.get(id)?.stickyWild?.destroy();
            this.currentBaseWildStickyDatas.delete(id);
        }
    }

    public static setBaseContainerVisibility(visible: boolean) {
        this.currentBaseWildStickyDatas.forEach(data => {
            data.container.visible = visible;
        });
    }

    public static shootBullet(bullet: PIXI.Sprite, container: Container) {
        return new Contract(resolve => {
            let tween = new TweenJS.Tween(bullet)
                .delay(this.bulletFlyTime)
                .onStart(() => {
                    soundManager.customEvent('Wild_Bullet_Fly');
                })
                .to({ x: bullet.x - 60 }, 220)
                .onComplete(() => {
                    const tween = new TweenJS.Tween(bullet)
                        .delay(150)
                        .onStart(() => {
                            let rand = 1 + Math.floor(Math.random() * 2);
                            soundManager.customEvent('Wild_Gunshot_' + rand);
                            this.playBlast(bullet.position.x + bullet.width / 2, bullet.position.y + 6, container);
                            resolve(null);
                        })
                        .to({ x: 2000 }, 1000)
                        .onComplete(() => {
                            bullet.destroy();
                        })
                        .start();
                        this.tweens.push(tween);
                })
                .start();
                this.tweens.push(tween);

            tween = new TweenJS.Tween(bullet.scale)
                .to({ x: 1.05, y: 1.05 }, 200)
                .delay(this.bulletZoomInTime)
                .easing(TweenJS.Easing.Linear.None)
                .start();
                this.tweens.push(tween);
        });
    }

    public static playBlast(x: number, y: number, container: Container, scale: number = 1) {
        const symbolBlastEffect = graphicsManager.createAnimation('wild_stick_blast_effect');
        container.addChild(symbolBlastEffect);
        symbolBlastEffect.position.set(x, y);
        symbolBlastEffect.anchor.set(0.5, 0.5);
        symbolBlastEffect.scale.set(scale, scale);
        symbolBlastEffect.gotoAndPlay(0);
        symbolBlastEffect.onComplete = () => {
            symbolBlastEffect.destroy();
        };
    }

    public static playBaseWildStickyAnimation(key: string) {
        const symbolStickyAnimation = graphicsManager.createAnimation('WW_stuck_win');
        const currentContainer = this.currentBaseWildStickyDatas.get(key).container;
        const bulletContainer = this.currentBaseWildStickyDatas.get(key).bulletContainer;
        const pos = currentContainer.position;        
        const scale = canvasManager.orientation === Orientation.PORTRAIT ? 0.7 : 1;        
        const containerOffset = new Point(320 * scale, 100 * scale);
        Layers.SymbolAnimations.container.addChild(symbolStickyAnimation);
        symbolStickyAnimation.position.set(pos.x - containerOffset.x, pos.y - containerOffset.y);
        symbolStickyAnimation.scale.set(scale, scale);
        symbolStickyAnimation.name = 'animation';
        symbolStickyAnimation.gotoAndPlay(0);
        Timer.setTimeout(() => {
            soundManager.customEvent('Wild_GunLoad');
        }, this.gunLoadTime);
        bulletContainer.position.set(0, 0);
        let tween = new TweenJS.Tween(bulletContainer)
            .to({ x: bulletContainer.position.x + 28 , y: bulletContainer.position.y + 5 }, 350)
            .easing(TweenJS.Easing.Linear.None)
            .onComplete(() => {
                const tween = new TweenJS.Tween(bulletContainer)
                    .delay(1200)
                    .to({ x: bulletContainer.position.x - 28, y: bulletContainer.position.y - 5 }, 350)
                    .easing(TweenJS.Easing.Linear.None)
                    .start();
                    this.tweens.push(tween);
            })
            .start();
            this.tweens.push(tween);

        tween = new TweenJS.Tween(bulletContainer.scale)
            .to({ x: 1.05, y: 1.05 }, 350)
            .onComplete(() => {
                const tween = new TweenJS.Tween(bulletContainer.scale)
                    .delay(1200)
                    .to({ x: 1, y: 1 }, 350)
                    .start();
                    this.tweens.push(tween);
            })
            .start();
            this.tweens.push(tween);

        const text = bulletContainer.getChildByName('remain') as PIXI.extras.BitmapText;
        const ellipse = bulletContainer.getChildByName('ellipse') as PIXI.extras.BitmapText;
        if (text) {
            const count = parseInt(text.text);
            const bullet = bulletContainer.getChildByName('bullet_' + (count - 1)) as PIXI.Sprite;

            if (bullet) {
                this.shootBullet(bullet, bulletContainer).then(() => {
                    this.playBlast(23, 12, bulletContainer, 0.7);
                    text.text = (count - 1).toString();
                    if (count - 1 === 0) {
                        ellipse.visible = false;
                        text.visible = false;
                    }
                });
            } else {
                Timer.setTimeout(() => {
                    this.playBlast(-85, -100, bulletContainer, 0.7);
                    text.text = (count - 1).toString();
                    if (count - 1 === 0) {
                        ellipse.visible = false;
                        text.visible = false;
                    }
                }, this.remaingTextChangeTime);
            }
        } else {
            this.destroyContainerById(key, false);
            symbolStickyAnimation.destroy();
        }

        Timer.setTimeout(() => {
            symbolStickyAnimation.destroy();
        }, 2000);
    }

    public static onLandWildStickyBehaviour(index: number) {
        this.wildStickyRecorder = GameInit.wildStickyRecorder;
        if (!this.wildStickyRecorder || !this.wildStickyRecorder.persistedProgress) return;
        const data = JSON.parse(this.wildStickyRecorder.persistedProgress.WILD_SPIN_DATA);
        const isFreepinSticky = GameInit.gameType === GameType.freeSpin;
        if (isFreepinSticky) {
            this.onLandFreespinStickyWild(this.wildStickyRecorder, index);
        } else {
            this.onLandBaseStickyWild(data, index);
        }
    }

    public static destoyBaseWildStickyDatas() {
        this.currentBaseWildStickyDatas.forEach(data => {
            const key = `symbol_${data.position.x}_${data.position.y}`;
            this.destroyContainerById(key, false);
        });
    }

    public static destroyFreespinWildStickyDatas() {
        this.currentFreeSpinWildStickyDatas.forEach(data => {
            const key = `symbol_${data.position.x}_${data.position.y}`;
            this.destroyContainerById(key, true);
        });
    }

    public static onEnterFreespins() {
        this.destoyBaseWildStickyDatas();
        this.destroyFreespinWildStickyDatas();
        this.clearStuckSymbols();
        this.killAllTweens();
        Components.matrix.resetSymbols();
        this.refreshSymbols();
    }

    public static onRecoveryWildStickyBehaviour() {
        this.clearStuckSymbols();
        Components.matrix.resetSymbols();
        this.wildStickyRecorder = GameInit.wildStickyRecorder;
        if (this.wildStickyRecorder?.persistedProgress?.WILD_SPIN_DATA) {
            const data = JSON.parse(this.wildStickyRecorder.persistedProgress.WILD_SPIN_DATA);
            const isFreepinSticky = GameInit.gameType === GameType.freeSpin;
            this.destroyFreespinWildStickyDatas();
            this.destoyBaseWildStickyDatas();
            if (isFreepinSticky) {
                this.currentFreeSpinWildStickyDatas = new Map<string, WildStickySymbolData>();
                this.onRecoveryFreespinStickyWild(data);
            } else {
                this.currentBaseWildStickyDatas = new Map<string, WildStickySymbolData>();
                this.onRecoveryBaseStickyWild(data);
            }
        }
    }

    public static onLandBaseStickyWild(data: any, index: number) {
        const totalBet = betModel.getTotalBet();
        if (!data[totalBet] || data[totalBet].length <= 0) {
            this.clearStuckSymbols();
            return;
        }
        for (let i = data[totalBet].length - 1; i >= 0; i--) {
            const remain: number = data[totalBet][i].remain;
            const position = this.positions[data[totalBet][i].position];
            if (this.checkIndexPosition(index, data[totalBet][i].position)) {
                const symbol = this.createBaseSticky(position.x, position.y, remain);
                if (symbol) {
                    symbol.land().execute();
                    this.landedWildCount++;
                    soundManager.customEvent('symbol_WW_land_' + (this.landedWildCount <= 5 ? this.landedWildCount : 5));
                }
            }
        }
    }

    public static onLandFreespinStickyWild(wildStickyRecord: WildStickyRecorder, index: number) {
        const stickyList = wildStickyRecord?.stickedWildsList;
        if (stickyList && stickyList.length > 0) {
            const container = Layers.Custom.get(GFCustomLayers.WILD_STICKY).container;
            container.parent.setChildIndex(container, 15);
            for (let i = 0; i < stickyList.length; i++) {
                const value: number = stickyList[i];
                const position = this.positions[value];
                if (this.checkIndexPosition(index, value)) {
                    const symbol = this.createFreeSticky(position.x, position.y);
                    if (symbol) {
                        symbol.land().execute();
                        this.landedWildCount++;
                        soundManager.customEvent('symbol_WW_land_' + (this.landedWildCount <= 5 ? this.landedWildCount : 5));
                    }
                }
            }
        }
    }

    public static checkIndexPosition(index: number, position: number): boolean {
        if (position <= index * 3 && position > (index - 1) * 3) {
            return true;
        }
        return false;
    }

    public static onRecoveryBaseStickyWild(data: any) {
        const totalBet = betModel.getTotalBet();
        data = data[totalBet];
        if (!data) return;
        for (let i = data.length - 1; i >= 0; i--) {
            const remain: number = data[i].remain;
            const position = this.positions[data[i].position];
            if (remain > 0) this.createBaseSticky(position.x, position.y, remain);
            else {
                const id = `symbol_${position.x}_${position.y}`;
                this.destroyContainerById(id, false);
            }
        }
    }

    public static onRecoveryFreespinStickyWild(data: any) {
        const totalBet = betModel.getTotalBet();
        data = data[totalBet];
        if (!data) return;
        for (let i = data.length - 1; i >= 0; i--) {
            const position = this.positions[data[i].position];
            this.createFreeSticky(position.x, position.y);
        }
    }

    public static createFreeSticky(x: number, y: number): SymbolComponent {
        const id = `symbol_${x}_${y}`;
        const stickyLayer = Layers.Custom.get(GFCustomLayers.WILD_STICKY);
        stickyLayer.container.parent.setChildIndex(stickyLayer.container, 15);

        if (!Components.matrix.stuckSymbols.has(id)) {
            const positionRect = Layers.MatrixContent.getPosition(id);
            const stickyWild = new SymbolComponent(new Point(x, y).clone());
            stickyWild.setSymbol('WW_freespin_stuck');
            stickyWild.setTransform(positionRect);
            stickyWild.setLayer(Layers.SymbolAnimations);
            stickyWild.lockLayer();
            stickyWild.updateTransform();
            stickyWild.addBehaviour(new LandAnimateSymbolBehaviour(stickyWild));
            stickyWild.addBehaviour(new AnimateSymbolBehaviour(stickyWild));
            stickyWild.addBehaviour(new DimSymbolBehaviour(stickyWild));
            Components.matrix.stuckSymbols.set(id, stickyWild);
            this.currentFreeSpinWildStickyDatas.set(id, new WildStickySymbolData(null, null, new Point(x, y), stickyWild));
            return stickyWild;
        }
        return null;
    }

    public static clearStuckSymbolsById(x: number, y: number) {
        const id = `symbol_${x}_${y}`;
        Components.matrix.stuckSymbols.get(id)?.destroy();
        Components.matrix.stuckSymbols?.delete(id);
    }

    public static clearStuckSymbols() {
        Components.matrix.stuckSymbols.forEach((symbol: SymbolComponent) => {
            symbol.destroy();
        });

        Components.matrix.stuckSymbols.clear();
    }

    public static createBaseSticky(x: number, y: number, remain: number): SymbolComponent {
        const id = `symbol_${x}_${y}`;
        const stickyLayer = Layers.Custom.get(GFCustomLayers.WILD_STICKY);
        stickyLayer.container.parent.setChildIndex(stickyLayer.container, 15);

        if (!Components.matrix.stuckSymbols.has(id)) {
            const positionRect = Layers.MatrixContent.getPosition(id);
            const stickyWild = new SymbolComponent(new Point(x, y).clone());
            const container = new Container();
            const scale = canvasManager.orientation === Orientation.PORTRAIT ? 0.7 : 1;
            container.scale.set(scale, scale);
            const bulletContainer = new Container();
            container.addChild(bulletContainer);
            stickyWild.setSymbol('WW');
            stickyWild.setTransform(positionRect);
            stickyWild.setLayer(Layers.SymbolAnimations);
            stickyWild.lockLayer();
            stickyWild.updateTransform();
            stickyLayer.container.addChild(container);
            stickyWild.addBehaviour(new LandAnimateSymbolBehaviour(stickyWild));
            stickyWild.addBehaviour(new AnimateSymbolBehaviour(stickyWild));
            stickyWild.addBehaviour(new DimSymbolBehaviour(stickyWild));

            const containerOffset = new Point(268 * scale, -15 * scale);

            if (remain > 0) {
                container.name = id;
                const globalPosition = Layers.MatrixContent.localToGlobal(stickyWild.getTransform());
                const pos = stickyLayer.globalToLocal(globalPosition);
                container.position.set(pos.x + containerOffset.x, pos.y + containerOffset.y);
                for (let i = 0; i < remain; i++) {
                    const bullet = graphicsManager.createSprite('particles/wild_bullet');
                    bulletContainer.addChild(bullet);
                    bullet.position.set(-35, 50 + i * 35);
                    bullet.name = 'bullet_' + i;
                }
                const belt = graphicsManager.createSprite('particles/wild_bullet_belt');
                belt.name = 'belt';
                bulletContainer.addChild(belt);
                belt.position.set(0, 0);
                const wild_random_no_Ellipse = graphicsManager.createSprite('particles/wild_random_no_Ellipse');
                bulletContainer.addChild(wild_random_no_Ellipse);
                wild_random_no_Ellipse.position.set(-20, -12);
                const remainText = new PIXI.extras.BitmapText(remain.toString(), { font: { size: 62, name: 'yellow' } });
                let pivot: Point = new Point(0.5, 0.5);
                Zoom(remainText, 1, 1, 0, 0, TweenJS.Easing.Quadratic.Out, 0, pivot).execute();
                bulletContainer.addChild(remainText);
                remainText.position.set(15, 27);
                remainText.name = 'remain';
                wild_random_no_Ellipse.name = 'ellipse';
            }
            this.currentBaseWildStickyDatas.set(id, new WildStickySymbolData(container, bulletContainer, new Point(x, y), stickyWild));
            Components.matrix.stuckSymbols.set(id, stickyWild);
            return stickyWild;
        }
        return null;
    }

    public static refreshSymbols() {     
        Components.matrix.resetSymbols();   
        Components.matrix.idleSymbols();
        for (const symbol of Components.matrix.getAllSymbols()) {
            symbol.setVisible(true);
            symbol.refresh();
        }
    }

    public static onBetUpdated() {
        const totalBet = betModel.getTotalBet();
        const record = gameState.getCurrentGame().getCurrentRecord();
        this.clearAllStuckSymbols();
        this.refreshSymbols();
      
        if (record) {
            const result = record.getResultsOfType(WildStickyRecorder);
            if (result && result.length > 0) {
                const wildSticky = result[0] as WildStickyRecorder;
                const s = wildSticky.persistedProgress?.WILD_SPIN_DATA;
                if (s && s.length > 0) {
                    const data = JSON.parse(s);
                    if (!data || !data[totalBet] || data[totalBet].length <= 0) {
                        return;
                    }
                    for (let i = data[totalBet].length - 1; i >= 0; i--) {
                        const remain: number = data[totalBet][i].remain;
                        const position = this.positions[data[totalBet][i].position];
                        this.createBaseSticky(position.x, position.y, remain);
                    }
                }
            }
        }
    }

    public static clearAllStuckSymbols() {
        this.destoyBaseWildStickyDatas();
        this.clearStuckSymbols();
        const stickyLayer = Layers.Custom.get(GFCustomLayers.WILD_STICKY);
        stickyLayer.clear();
    }
}



// WEBPACK FOOTER //
// ./src/states/wild-sitcky.ts